import * as React from 'react';
import { inject } from 'mobx-react';
import { Footer, Header } from '@gc/global-components';
import { CustomerNotifications } from 'components/CustomerNotification';
import { CustomerNotificationLocation } from '@shared-ui/customer-notifications';
import { VaContainer } from 'components/VirtualAgentControl';
import { HelpArticlesWrapper as HelpArticles } from 'components/Articles';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';
import { EVENTS } from 'utils/logger-events';
import { ErrorBoundary } from 'bernie-view';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';
import { egClickStreamConstants } from 'common/constant';
import { VirtualAgentControlStore } from 'stores/virtual-agent-control-store';
import { prepareClickStreamPayload } from 'common/helper';
import { gql, useQuery } from '@apollo/client';
import { useBexApi } from '@shared-ui/bex-api-context';
import { getHelpCenterConfigQuery, ISRequestPayloadInput, ISViewType } from 'common/__generated__/api/types';
import { WelcomeBanner } from 'components/WelcomeBanner';
import { IntentButton } from 'components/IntentButtons';
import { ClientLogger } from 'bernie-client';
import { TaapAcademy } from 'components/TaapAcademy';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { processFeatureGateOverride } from 'utils/featureGatesOverride';
import { Trips } from 'components/TripInfo';
import { HelpCenterSearch, IntelligentSearch } from '@shared-ui/retail-help-center-search';
import { SignInCta } from 'components/SignInCta/sign-in-cta';
import { AppDownloadBanner } from 'components/AppDownloadBanner';
import { Qualtrics } from '@shared-ui/customer-qualtrics';
import { VirtualAgentContextProvider } from '@shared-ui/retail-help-center-util';
import { Tripmtwo } from '@shared-ui/retail-help-center-trip';
import { PubSubSignalManager } from '@shared-ui/pubsub-signal-manager';
import { SignalContextProvider } from '@shared-ui/core-signal-manager/context';
import { ContactUsStateProvider } from '@shared-ui/retail-help-center-util';

const logger = ClientLogger.getLoggerWithIdentifier('helpcenter');
export interface HelpCenterProps {
  virtualAgentControlStore?: VirtualAgentControlStore;
}
const QUERY = gql`
  query getHelpCenterConfig($context: ContextInput!) {
    helpCenterPersonalization(context: $context) {
      helpCenterPersonalizedView {
        vaLayoutVariant
        banner {
          ...BannerFields
        }
        ...IntentConfigFields
        isVacEnabled
        taapAcademyComponent {
          ...TaapAcademyComponentFields
        }
        travelerTripDetailedOverview {
          ...TripInfoDetailFields
        }
        helpArticleTitle
      }
      helpCenterSignInCTAView {
        ...SignInCTAFields
      }
    }
  }
  ${WelcomeBanner.fragment}
  ${IntentButton.fragment}
  ${SignInCta.fragment}
  ${TaapAcademy.fragment}
  ${Trips.fragment}
`;

const HelpCenter: React.FC<HelpCenterProps> = inject('virtualAgentControlStore')(
  ({ virtualAgentControlStore }: HelpCenterProps) => {
    const trackClick = useEgClickstream();
    const { context } = useBexApi();
    const { exposure: intelligentSearchExposure, logExperiment: logIntelligentSearchExperiment } = useExperiment(
      'Help_Center_Intelligent_Search_w_Follow_Up_Actions_AB_Test'
    );

    const { exposure: cometExposure, logExperiment: logCometExperiment } = useExperiment(
      'Help_Center_Trip_View_and_Sign_In_COMET_Rollout'
    );
    const { exposure: intelligentSearchM2Exposure, logExperiment: logIntelligentSearchM2Experiment } = useExperiment(
      'Help_Center_Web_Intelligent_Search_M2_UX'
    );
    const { exposure: vrboExposure, logExperiment: logVrboExperiment } = useExperiment(
      'Help_Center_Trip_View_and_Sign_In_Vrbo_Rollout'
    );
    const { exposure: helpArticlesM2Exposure, logExperiment: logHelpArticlesM2Experiment } = useExperiment(
      'Help_Center_Web_Articles_UX_M2'
    );
    const { exposure: contactUsExperienceExposure, logExperiment: logNewContactUsEnabled } = useExperiment(
      'Contact_Us_Redesign_MVP_BEX_HCOM'
    );
    const { exposure: multiTurnExposure, logExperiment: logMultiTurnExperiment } = useExperiment(
      'AI_Advanced_Support_Multiturn_for_UNKOWN_and_SPEAK_TO_AGENT'
    );
    const { data } = useQuery<getHelpCenterConfigQuery>(QUERY, { variables: { context } });
    const [shouldHideNonISComponents, setShouldHideNonISComponents] = React.useState(false);
    React.useEffect(() => {
      logger.logEvent(EVENTS.UI_MESSAGE, {
        message: `Variant HELPCENTER_COMPONENT_WITH_CONFIG loaded for ${context.siteId}`,
      });
    }, [logger]);
    const signedInUser = !data?.helpCenterPersonalization?.helpCenterSignInCTAView;
    const signalManagerInstance = React.useMemo(() => new PubSubSignalManager(), []);
    const shouldShowAllHelpCenterComponents =
      (!shouldHideNonISComponents && intelligentSearchM2Exposure.bucket === 1) ||
      intelligentSearchM2Exposure.bucket === 0;

    const [isPayload, setISPayload] = React.useState<ISRequestPayloadInput>({
      viewType: ISViewType.DEFAULT,
      searchTerm: '',
    });

    React.useEffect(() => {
      if (data?.helpCenterPersonalization) {
        logIntelligentSearchExperiment(intelligentSearchExposure);
        logIntelligentSearchM2Experiment(intelligentSearchM2Exposure);
        logCometExperiment(cometExposure);
        logVrboExperiment(vrboExposure);
        logHelpArticlesM2Experiment(helpArticlesM2Exposure);
        logNewContactUsEnabled(contactUsExperienceExposure);
        logMultiTurnExperiment(multiTurnExposure);
        processFeatureGateOverride();
        const { egClickStreamEventNames, egClickStreamEventTypes, egClickStreamEventCategories } =
          egClickStreamConstants;
        const clickStreamEvent = prepareClickStreamPayload(
          virtualAgentControlStore,
          egClickStreamEventNames.helpCenterPageView,
          egClickStreamEventTypes.pageView,
          egClickStreamEventCategories.helpCenter,
          undefined
        );
        trackClick(clickStreamEvent);
      }
    }, [data]);

    return (
      <ErrorBoundary label="Help center Error Boundary" fallback={<div />}>
        <SignalContextProvider manager={signalManagerInstance}>
          <ContactUsStateProvider>
            <VirtualAgentContextProvider>
              <div className={'middle-bg'}>
                <Header pageName="help-center" />
                <Experiment name="HELP_CENTER_PWA_QUALTRICS">
                  <ExperimentControl />
                  <ExperimentVariant bucket={1}>
                    <Qualtrics skipSsr />
                  </ExperimentVariant>
                </Experiment>
                <div role="main" className={'middle-con global_navigation__header__default__max_width'}>
                  <Experiment name="HELP_CENTER_DISPLAY_TRAVEL_ADVISORY">
                    <ExperimentControl></ExperimentControl>
                    <ExperimentVariant bucket={1}>
                      <CustomerNotifications location={CustomerNotificationLocation.AFTER_HEADER} />
                    </ExperimentVariant>
                  </Experiment>
                  {shouldShowAllHelpCenterComponents && (
                    <>
                      {data?.helpCenterPersonalization?.helpCenterPersonalizedView?.banner && (
                        <WelcomeBanner data={data?.helpCenterPersonalization?.helpCenterPersonalizedView?.banner} />
                      )}
                    </>
                  )}
                  {(data?.helpCenterPersonalization.helpCenterPersonalizedView.isVacEnabled ?? false) && (
                    <VaContainer
                      data={data.helpCenterPersonalization.helpCenterPersonalizedView}
                      signedInUser={signedInUser}
                      userHasTrip={
                        data?.helpCenterPersonalization?.helpCenterPersonalizedView?.travelerTripDetailedOverview
                          ?.allTrips?.length > 0
                      }
                    />
                  )}
                  {/* If multi turn is turned on we check for signedInUser to show 
                    intelligentSearch else default searchbar is shown by HelpCenterSearch */}
                  <EGDSSpacing margin={{ blockend: 'eight' }}>
                    <div>
                      <Experiment name="AI_Advanced_Support_Multiturn_for_UNKOWN_and_SPEAK_TO_AGENT">
                        <ExperimentControl>
                          <HelpCenterSearch ssr onToggleISEngaged={setShouldHideNonISComponents} />
                        </ExperimentControl>
                        <ExperimentVariant bucket={1}>
                          {signedInUser ? (
                            <IntelligentSearch
                              ssr
                              isPayload={isPayload}
                              onClick={setISPayload}
                              onToggleISEngaged={setShouldHideNonISComponents}
                            />
                          ) : (
                            <HelpCenterSearch ssr onToggleISEngaged={setShouldHideNonISComponents} />
                          )}
                        </ExperimentVariant>
                      </Experiment>
                    </div>
                  </EGDSSpacing>
                  {shouldShowAllHelpCenterComponents && (
                    <>
                      {data?.helpCenterPersonalization?.helpCenterSignInCTAView && (
                        <SignInCta data={data.helpCenterPersonalization.helpCenterSignInCTAView} />
                      )}
                      <div>
                        {data?.helpCenterPersonalization.helpCenterPersonalizedView.taapAcademyComponent && (
                          <EGDSSpacing margin={{ blockstart: 'twelve', blockend: 'four' }}>
                            <TaapAcademy
                              data={data?.helpCenterPersonalization.helpCenterPersonalizedView.taapAcademyComponent}
                            />
                          </EGDSSpacing>
                        )}
                      </div>
                      {signedInUser && (
                        <Experiment name="Help_Center_Trips_View_Feature_Toggle">
                          <ExperimentControl />
                          <ExperimentVariant bucket={1}>
                            <Tripmtwo inputs={null} />
                          </ExperimentVariant>
                        </Experiment>
                      )}
                      {signedInUser && (
                        <Experiment name="Help_Center_Web_M2">
                          <ExperimentControl />
                          <ExperimentVariant bucket={1}>
                            <Tripmtwo inputs={null} />
                          </ExperimentVariant>
                        </Experiment>
                      )}
                      {signedInUser && (
                        <Experiment name="Help_Center_Trip_View_and_Sign_In_COMET_Rollout">
                          <ExperimentControl />
                          <ExperimentVariant bucket={1}>
                            <Tripmtwo inputs={null} />
                          </ExperimentVariant>
                        </Experiment>
                      )}
                      {signedInUser && (
                        <Experiment name="Help_Center_Trip_View_and_Sign_In_Vrbo_Rollout">
                          <ExperimentControl />
                          <ExperimentVariant bucket={1}>
                            <Tripmtwo inputs={null} />
                          </ExperimentVariant>
                        </Experiment>
                      )}
                      <EGDSSpacing margin={{ blockstart: 'eight' }}>
                        <div>
                          <HelpArticles />
                        </div>
                      </EGDSSpacing>
                      <Experiment name="Help_Center_Web_Landing_Page_UX_M2">
                        <ExperimentControl>
                          <EGDSSpacing padding={{ blockstart: 'two' }}>
                            <AppDownloadBanner />
                          </EGDSSpacing>
                        </ExperimentControl>
                        <ExperimentVariant bucket={1}>
                          <div style={{ height: '56px' }} />
                        </ExperimentVariant>
                      </Experiment>
                    </>
                  )}
                </div>
                <Footer />
              </div>
            </VirtualAgentContextProvider>
          </ContactUsStateProvider>
        </SignalContextProvider>
      </ErrorBoundary>
    );
  }
);

HelpCenter.displayName = 'HelpCenter';

export default HelpCenter;
